import React, { useEffect, useState } from "react";
import { Button, Col, Row, Stack } from "react-bootstrap";
import SelectObject from "../../ui/SelectObject";
import SelectObjectTypeahead from "../../ui/SelectObjectTypeahead";
import Input from "../../ui/Input";
import { fetchResourceCategories, fetchResourceTypes, postResource, postResourceCategory } from "../../../services/httpClient";
import { unsavedChanges } from "../../../App";
import useApp from "../../../utils/useApp";

interface AddResourceState {
  setOrgToSave: any;
  typeFilter: string | null;
  setTypeFilter: any;
  categoryFilter: string | null;
  setCategoryFilter: any;
  setCategoryFilterName: any;
  setShowAddResource: any;
  getResources: any;
  getAllResourceCategories: any;
  setErrMsg: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const AddResource = ({
  setOrgToSave,
  typeFilter,
  setTypeFilter,
  categoryFilter,
  setCategoryFilter,
  setCategoryFilterName,
  setShowAddResource,
  getResources,
  getAllResourceCategories,
  setErrMsg,
  setShowToast,
  setToastText,
  setToastBody,
}: AddResourceState) => {
  const { auth }: any = useApp();
  const id = auth?.profile?.userOrgID;
  const [resourceTypes, setResourceTypes] = useState<any[]>([]);
  const [resourceCategories, setResourceCategories] = useState<any[]>([]);
  const [allowSaveResource, setAllowSaveResource] = useState<boolean>(false);
  const [resourceType, setResourceType] = useState<string | null>();
  const [resourceCategory, setResourceCategory] = useState<string | null>();
  const [resourceName, setResourceName] = useState<string>("");
  const [resourceQuantity, setResourceQuantity] = useState<number>();

  useEffect(() => {
    const validInput = resourceType && resourceName?.trim() && resourceQuantity;

    validInput ? setAllowSaveResource(true) : setAllowSaveResource(false);
  }, [resourceType, resourceName, resourceQuantity]);

  useEffect(() => {
    console.log("AddResource useEffect called");
    console.log(categoryFilter, resourceCategory);
    async function fetchData() {
      await Promise.all([getResourceTypes(), getResourceCategories()]);
      setResourceType(typeFilter || null);
      setResourceCategory(categoryFilter || null);
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setResourceType(typeFilter || null);
  }, [typeFilter]);

  useEffect(() => {
    setResourceCategory(categoryFilter || null);
  }, [categoryFilter]);

  const getResourceTypes = () => {
    fetchResourceTypes()
      .then((res: any) => {
        const types = res[0].data;
        setResourceTypes(types);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const getResourceCategories = async () => {
    await fetchResourceCategories()
      .then((res: any) => {
        let cats = res[0].data;
        cats = [{ id: 0, key: "0", name: "None" }, ...cats];
        setResourceCategories(cats);
        console.log("Resource Categories:", cats);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  const onClickAddResourceCategory = (newCategoryName: string) => {
    const newCategory = newCategoryName.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

    return postResourceCategory({ name: newCategory })
      .then((res: any) => {
        setShowToast(true);
        setToastText("Category Created");
        setToastBody(newCategory);
        getResourceCategories();
        const cat = `${id}_${newCategory.toLowerCase()}`;
        setResourceCategory(cat);
        getAllResourceCategories();
        return { key: cat, name: newCategory, id: res[0].data.category_id };
      })
      .catch((err: any) => {
        console.log(err);
        throw err;
      });
  };

  const onClickAddResourceCancel = () => {
    unsavedChanges.value = false;
    setErrMsg("");
    setShowAddResource(false);
    setResourceType(null);
    setResourceCategory(null);
    setResourceName("");
    setResourceQuantity(undefined);
    setOrgToSave(false);
    getResources(false);
  };

  const onClickSaveResource = (isDone: boolean = false) => {
    setErrMsg("");

    const newResource = {
      type_key: resourceType,
      category_key: resourceCategory,
      name: resourceName,
      quantity: resourceQuantity,
    };

    postResource(newResource)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Resource Created");
        setToastBody(resourceName);
        setResourceName("");
        setResourceQuantity(undefined);
        unsavedChanges.value = false;
        setAllowSaveResource(false);
        setOrgToSave(false);
        if (isDone) {
          setResourceType(null);
          setResourceCategory(null);
          setShowAddResource(false);
        }
        getResources();
      })
      .catch((err: any) => {
        setErrMsg(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const updateName = (e: any) => {
    setResourceName(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateQuantity = (e: any) => {
    setResourceQuantity(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateType = (e: any) => {
    if (e.target.value === "0") {
      setResourceType(null);
      setTypeFilter(null);
    } else {
      setResourceType(e.target.value);
      setTypeFilter(e.target.value);
    }
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  const updateCategory = (e: any) => {
    setResourceCategory(e?.key);
    setCategoryFilter(e?.key);
    setCategoryFilterName(e?.name);
    unsavedChanges.value = true;
    setOrgToSave(true);
  };

  return (
    <Stack gap={3} className="mb-3">
      <Row>
        <Col xs={6} lg={4}>
          <SelectObject
            label="Type"
            value={resourceType}
            options={resourceTypes}
            onUpdate={(e: any) => updateType(e)}
            required={true}
            hasFocus={true}
          />
        </Col>
        <Col xs={6} lg={4}>
          <SelectObjectTypeahead
            initialOption="Search or type to create"
            label="Category"
            value={resourceCategory}
            options={resourceCategories}
            onUpdate={(e: any) => updateCategory(e)}
            allowCreate={true}
            onCreateOption={async (inputValue: string) => {
              const newCategory = await onClickAddResourceCategory(inputValue);
              return newCategory;
            }}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={9} lg={6}>
          <Input label="Name" type="text" value={resourceName} onUpdate={(e: any) => updateName(e)} required={true} />
        </Col>
        <Col xs={3} lg={2}>
          <Input label="Quantity" type="number" value={resourceQuantity} onUpdate={(e: any) => updateQuantity(e)} required={true} />
        </Col>
      </Row>
      <Row>
        <Col xs={12} lg={8}>
          <Stack direction="horizontal" gap={3}>
            <Button className="app-secondary-color button" onClick={() => onClickSaveResource(false)} disabled={!allowSaveResource}>
              Save and Add Another
            </Button>
            <div className="vr" />
            <Button className="app-secondary-color button" onClick={() => onClickSaveResource(true)} disabled={!allowSaveResource}>
              Save and Close
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickAddResourceCancel()}>
              Cancel
            </Button>
          </Stack>
        </Col>
      </Row>
    </Stack>
  );
};

export default AddResource;
